import * as React from 'react';
import {connect} from 'react-redux';

import moment from 'moment';
import type {Dispatch} from 'redux';

import {ActivityTypes} from 'nutshell-core/activity-types';
import {getDateRoundedToQuarterHour} from 'nutshell-core/date-time';
import {Session} from 'nutshell-core/session';
import type {ActivityType} from 'nutshell-core/types';

import type {NutshellState} from '../../../store';

import * as createNewActions from '../../creator/creator-actions';
import type {FlashMessage} from '../../creator/types';
// @ts-expect-error
import * as dashboardActions from '../../master-dashboard/dashboard-actions';

import {handleFormSubmissionError} from '../handle-form-submission-error';

import {ActivityFormConnected} from './activity-form-connected';
import {getLinksForSubmission} from './helpers';

type OwnProps = {
    onSaveCallback: (entity: any, jumpToUrl: boolean) => any;
    onCancelClick: () => void;
};

type StateProps = {
    initialValues: any;
    enableReinitialize: boolean;
    keepDirtyOnReinitialize: boolean;
    activityTypes: ActivityType[];
};

type DispatchProps = {
    pushFlashMessage: (arg1: FlashMessage) => void;
    onSubmit: (arg1: any) => any;
    onSubmitSuccess: (arg1: any) => any;
    onSubmitFail: (errors: any[], dispatchFunction: Dispatch<any>, jqXHRError: any) => any;
};

type Props = OwnProps & StateProps & DispatchProps;

const NOW_DATE_ROUNDED = getDateRoundedToQuarterHour(new Date());
const initialActivityTimeValue = {
    isAllDay: false,
    startTime: moment(NOW_DATE_ROUNDED).add(15, 'minutes').toDate(),
    endTime: moment(NOW_DATE_ROUNDED).add(30, 'minutes').toDate(),
} as const;

const mapStateToProps = function (state: NutshellState): StateProps {
    const activityTypes = ActivityTypes.getAllLoggable(state);

    return {
        // Used by redux-form
        initialValues: {
            activityTime: initialActivityTimeValue,
            // TODO: This should be a `getDefault` selector function
            // @ts-expect-error
            activityType: activityTypes.find((at) => at.name === 'Meeting'),
            // TODO: This seems to take a while to populate, and is incomplete when first loading sometimes.
            links: {
                participants: [Session.getSessionStateUser(state)],
            },
        },
        // The initial value for assignee is initially empty when the page first loads,
        // and is only fully populated after a rest request to the backend.
        // By enabling reinitialization, we can update the initial value for assingee
        // after that response comes back.
        enableReinitialize: true,
        // In case the user has made any changes to the form before the session
        // user response came back, this will keep their changes and only change
        // the assignee if it's pristine.
        keepDirtyOnReinitialize: true,
        activityTypes: activityTypes,
    };
};

// @ts-expect-error
function mapDispatchToProps(dispatch, ownProps: OwnProps): DispatchProps {
    return {
        // Not actually using dispatch yet, this is a temp solution
        onSubmit: (data) => {
            // TODO: pull this sanitization out into a separate function
            const activity = {
                ...data,
                startTime: moment(data.activityTime.startTime).unix(),
                endTime: moment(data.activityTime.endTime).unix(),
                isAllDay: data.activityTime.isAllDay,
                activityType: data.activityType.id,
                links: getLinksForSubmission(data.links, 'activities'),
            } as const;

            return (
                $.ajax({
                    url: '/rest/activities',
                    type: 'POST',
                    dataType: 'json',
                    contentType: 'application/json',
                    data: JSON.stringify({data: activity}),
                })
                    // Add the jqXHR object and activity data to the response, so that it can
                    // be used in the onSubmitSuccess handler
                    .then((response, status, jqXHR) => {
                        return {response, jqXHR, activity};
                    })
                    .fail(() => {
                        // TODO: Figure out how we want to display errors to the user. This
                        // will almost certainly involve looking at that actual jqXHR object,
                        // but we need to work with that object here and return out something
                        // that redux form is okay with. Because, the jqXHR object causes a
                        // 'Maximum call stack size exceeded error'
                        return 'Error saving';
                    })
            );
        },
        onSubmitSuccess: ({response, jqXHR, postData}: any) => {
            // If we have a response back, lets use it
            const activity =
                jqXHR.status !== 204
                    ? // Pretty big assumption here that the response will have the newly
                      // created activity at index 0
                      response.activities[0]
                    : postData;

            // Save callback
            ownProps.onSaveCallback(activity, false);

            if (location.pathname.startsWith('/dashboard')) {
                if (activity.id) {
                    dispatch(dashboardActions.refetchActivity(activity.id));
                }
            }
        },
        onSubmitFail: (errors, dispatchFunction, jqXHRError: any) => {
            handleFormSubmissionError(dispatch, jqXHRError);
        },
        pushFlashMessage: (flashMessage: FlashMessage) => {
            dispatch(createNewActions.pushFlashMessage(flashMessage));
        },
    };
}

export class ActivityFormComponent extends React.PureComponent<Props> {
    render() {
        return (
            <ActivityFormConnected
                // @ts-expect-error
                onSaveCallback={this.props.onSaveCallback}
                initialValues={this.props.initialValues}
                enableReinitialize={this.props.enableReinitialize}
                keepDirtyOnReinitialize={this.props.keepDirtyOnReinitialize}
                activityTypes={this.props.activityTypes}
                pushFlashMessage={this.props.pushFlashMessage}
                onCancelClick={this.props.onCancelClick}
                onSubmit={this.props.onSubmit}
                onSubmitSuccess={this.props.onSubmitSuccess}
                onSubmitFail={this.props.onSubmitFail}
            />
        );
    }
}

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(mapStateToProps, mapDispatchToProps);
export const ActivityForm = connector(ActivityFormComponent);
